import { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import QrIcon from '@mui/icons-material/QrCodeScanner';
import { IRestaurant } from '../../../repository/vendor';
import { timeUnix } from '../../../utility/k_time';
import { qlubSessionStorage } from '../../../service/storage/sessionStorage';
import MultiLocale from '../../../component/MultiLocale';
import { useQlubRouter } from '../../../hook/router';
import { useTranslation } from '../../../hook/translations';
import { ThemeModeEnum } from '../../config';
import { LogoWhite } from '../../../component/SVG';

import styles from './screenTimeout.module.scss';

const key = 'qlub.screenTimeout';

export const useScreenTimeout = (vendor: IRestaurant | undefined) => {
    const { lang, routerReplace, query, pathname, theme } = useQlubRouter();
    const { t } = useTranslation('common');
    const [showTimeout, setShowTimeout] = useState(query.screenTimeout === 'true');

    const setTime = useCallback((time: number) => {
        qlubSessionStorage.set(key, String(time));
    }, []);

    const getTime = useCallback((): number => {
        return Number(qlubSessionStorage.get(key) || '0');
    }, []);

    const checkTimeout = useCallback(() => {
        if (!vendor || document.hidden === true) {
            return;
        }

        const timeout = vendor.config?.screenTimeout || 0;
        if (timeUnix() - getTime() > timeout) {
            setShowTimeout(true);
            routerReplace(pathname, { ...query, screenTimeout: 'true' });
        }
    }, [vendor]);

    useEffect(() => {
        if (!vendor || !vendor.config?.screenTimeout) {
            return () => {
                //
            };
        }

        window.addEventListener('visibilitychange', checkTimeout);
        window.addEventListener('focus', checkTimeout);

        return () => {
            window.removeEventListener('visibilitychange', checkTimeout);
            window.removeEventListener('focus', checkTimeout);
        };
    }, [checkTimeout]);

    useEffect(() => {
        if (!vendor || !vendor.config?.screenTimeout || showTimeout) {
            return () => {
                //
            };
        }

        if (!getTime()) {
            setTime(timeUnix());
        }
        checkTimeout();
        const interval = setInterval(() => {
            checkTimeout();
        }, 1000);
        return () => {
            clearInterval(interval);
        };
    }, [vendor, checkTimeout, showTimeout]);

    const text = useMemo(() => {
        if (!vendor || !vendor.config?.screenTimeout) {
            return null;
        }

        if (!vendor.config.screenTimeoutText) {
            return t('Please scan the QR again');
        }

        return <MultiLocale value={vendor.config.screenTimeoutText} lang={lang} />;
    }, [vendor, lang]);

    const screenTimeoutContent = useMemo(() => {
        if (!showTimeout) {
            return null;
        }

        return (
            <div className={classNames(styles.screenTimeout, { [styles.black]: theme === ThemeModeEnum.Black })}>
                <div className={styles.text}>{text}</div>
                <div className={styles.icon}>
                    <QrIcon />
                </div>
                {!vendor?.config?.hideLogo && (
                    <div className={styles.footer}>
                        <LogoWhite />
                    </div>
                )}
            </div>
        );
    }, [showTimeout, text]);

    return {
        screenTimeoutContent,
    };
};
